import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

interface VariantStats {
  campaignId: number
  totalReplies: number
  autoReply: number
  ooo: number
  humanReplies: number
  positiveReplies: number
  nullCategory: number
}

interface CampaignVariantData {
  variantStats: VariantStats[]
  totalStats: {
    totalReplies: number
    autoReply: number
    ooo: number
    humanReplies: number
    positiveReplies: number
  }
}

export function useCampaignVariants(campaignId: string, campaignName: string) {
  return useQuery({
    queryKey: ['campaign-variants', campaignId],
    queryFn: async (): Promise<CampaignVariantData> => {
      if (!campaignId || !campaignName) {
        return {
          variantStats: [],
          totalStats: { totalReplies: 0, autoReply: 0, ooo: 0, humanReplies: 0, positiveReplies: 0 }
        }
      }

      try {
        // Get all variants for this campaign
        const { data: variants, error: variantsError } = await supabase
          .from('ab_test_segments')
          .select(`
            id,
            name,
            ab_test_variants (
              id,
              name,
              smartlead_campaign_id,
              configuration
            )
          `)
          .eq('campaign_id', campaignId)

        if (variantsError) {
          console.error('Error fetching variants:', variantsError)
          throw variantsError
        }

        // Extract SmartLead campaign IDs from variants
        const slCampaignIds: number[] = []
        variants?.forEach(segment => {
          segment.ab_test_variants?.forEach((variant: any) => {
            if (variant.smartlead_campaign_id && /^\d+$/.test(variant.smartlead_campaign_id)) {
              slCampaignIds.push(parseInt(variant.smartlead_campaign_id))
            } else if (variant.configuration?.campaign_url) {
              const match = variant.configuration.campaign_url.match(/email-campaign\/(\d+)/)
              if (match && match[1]) {
                slCampaignIds.push(parseInt(match[1]))
              }
            }
          })
        })

        if (slCampaignIds.length === 0) {
          return {
            variantStats: [],
            totalStats: { totalReplies: 0, autoReply: 0, ooo: 0, humanReplies: 0, positiveReplies: 0 }
          }
        }

        // Use server-side RPC for aggregation — no row limits, consolidated obsync + masterinbox
        const { data: rpcResult, error: rpcError } = await supabase
          .rpc('get_variant_reply_breakdown', { target_campaign_ids: slCampaignIds })

        if (rpcError) {
          console.error('Error fetching variant breakdown:', rpcError)
          throw rpcError
        }

        const result = rpcResult as any

        // Map RPC response to expected format
        const variantStats: VariantStats[] = (result.variants || []).map((v: any) => ({
          campaignId: v.campaign_id,
          totalReplies: v.total_replies || 0,
          autoReply: v.auto_reply || 0,
          ooo: v.ooo || 0,
          humanReplies: v.human_replies || 0,
          positiveReplies: v.positive_replies || 0,
          nullCategory: v.null_category || 0,
        }))

        const totals = result.totals || {}
        const totalStats = {
          totalReplies: totals.total_replies || 0,
          autoReply: totals.auto_reply || 0,
          ooo: totals.ooo || 0,
          humanReplies: totals.human_replies || 0,
          positiveReplies: totals.positive_replies || 0,
        }

        return { variantStats, totalStats }
      } catch (error) {
        console.error('Error in useCampaignVariants:', error)
        throw error
      }
    },
    enabled: !!campaignId && !!campaignName,
    staleTime: 5 * 60 * 1000,
  })
}
